import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { Loading } from "src/screens";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { UI } from "@wwimmo/ui";
import i18next from "i18next";
import styles from "./Login.module.css";

interface LoginForm {
    email: string;
    password: string;
}

export const COOKIE_PROPERTY_IS_LOGGED_IN = "isLoggedIn";
export const COOKIE_PROPERTY_REFRESH_TOKEN = "refreshToken";
export const COOKIE_EXPIRES = "expires";
export const setCookieRow = (name: string, value: string) => {
    document.cookie = `${name}=${value};`;
};

enum RELOGIN_STATES {
    PENDING = 0,
    ACTIVE = 1,
    DONE = 2
}

enum ERP_LOGIN_ERROR_TYPE {
    NONE = 0,
    IT2 = "immotop2",
    RIMO = "rimo"
}

const FILEPATH_ERP_HINT_IMMOTOP2 = "/erp-hints/einrichtung_portal_immotop2.pdf";
const FILEPATH_ERP_HINT_RIMOR5 = "/erp-hints/einrichtung_abnahme_app_rimor5.pdf";

const LoginBase = () => {
    const { t } = useTranslation();
    const { navStore, authStore, easyContactStore } = useContext(RootStoreContext);
    const [relogin, setRelogin] = useState<RELOGIN_STATES>(RELOGIN_STATES.PENDING);
    const [login, setLogin] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [erpUserLoginError, setErpUserLoginError] = useState<ERP_LOGIN_ERROR_TYPE>(ERP_LOGIN_ERROR_TYPE.NONE);
    const {
        register,
        handleSubmit,
        watch,

        formState: { errors }
    } = useForm<LoginForm>({ shouldUnregister: false });

    useEffect(() => {
        runInAction(() => {
            navStore.setBackbutton(null);
            const customerName = getComputedStyle(document.documentElement).getPropertyValue("--wwcustomername");
            navStore.setTitle(relogin === RELOGIN_STATES.DONE ? t("screens.auth.login") : "");
            navStore.setTabTitle(`${customerName} Portal - ${t("screens.auth.login")}`);
        });
    }, [relogin, navStore, t]);

    useEffect(() => {
        // re-login

        async function doRelogin() {
            try {
                const wasReloginSuccessful = await authStore.authClient.relogin(authStore.refresh);

                if (!wasReloginSuccessful) {
                    setRelogin(RELOGIN_STATES.DONE);
                } else if (!easyContactStore.isLoggedIn) {
                    easyContactStore.setIsLoggedIn(true);
                }
            } catch (error) {
                setRelogin(RELOGIN_STATES.DONE);
            }
        }
        if (authStore.loaded && relogin === RELOGIN_STATES.PENDING) {
            setRelogin(RELOGIN_STATES.ACTIVE);
            doRelogin();
        }
    }, [relogin, easyContactStore, authStore.loaded, authStore.refresh, authStore.authClient]);

    const setLoginCookies = useCallback(() => {
        const dateNow = new Date();
        const expireDate = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate(), 23, 59, 59);
        setCookieRow(COOKIE_PROPERTY_IS_LOGGED_IN, `true;${COOKIE_EXPIRES}=${expireDate}`);
        setCookieRow(COOKIE_PROPERTY_REFRESH_TOKEN, authStore.refresh ?? "0");
    }, [authStore.refresh]);

    const doLogin = useCallback(
        async (formData) => {
            setLogin(true);
            setError(undefined);
            try {
                await authStore.authClient.login(formData.email, formData.password);
                setLoginCookies();
                easyContactStore.setShowLogin(false);
                easyContactStore.setIsLoggedIn(true);
            } catch (error: any) {
                setErpUserLoginError(ERP_LOGIN_ERROR_TYPE.NONE);
                const isIds4Error = i18next.exists(`error.${error.message}`);
                const isAuthError = i18next.exists(`error.auth.${error.message}`);

                let errorMessage = error.message;

                if (isIds4Error) {
                    const errorMessageParts: string[] = error.message.split("_");
                    const erp = errorMessageParts[errorMessageParts.length - 1];

                    errorMessage = t(`error.${error.message}`);

                    switch (erp) {
                        case ERP_LOGIN_ERROR_TYPE.IT2:
                            setErpUserLoginError(ERP_LOGIN_ERROR_TYPE.IT2);
                            break;
                        case ERP_LOGIN_ERROR_TYPE.RIMO:
                            setErpUserLoginError(ERP_LOGIN_ERROR_TYPE.RIMO);
                            break;
                        default:
                            break;
                    }
                }

                if (isAuthError) {
                    errorMessage = t(`error.auth.${error.message}`);
                }

                setLogin(false);
                setError({ name: "Error", message: errorMessage });
                authStore.authClient.logout();
            }
        },
        [authStore.authClient, t, easyContactStore, setLoginCookies]
    );

    const ErpInformationLink = (props: any) => (
        <a className={styles.erpInformationLink} href={props.filepath} rel="noreferrer" target="_blank">
            {t("labels.additional_information")}
        </a>
    );

    const onKeyDown = useCallback(
        (e: any) => {
            if (e.key === "Enter") {
                e.preventDefault();
                if (watch("email").length > 0 && watch("password").length > 0) {
                    doLogin({ email: watch("email"), password: watch("password") });
                }
            }
        },
        [doLogin, watch]
    );

    useEffect(() => {
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onKeyDown]);

    if (!authStore.loaded || login || relogin !== RELOGIN_STATES.DONE) {
        return <Loading noLogo noBackgroundChange />;
    } else {
        return (
            <UI.Container className="login">
                <UI.Form onSubmit={handleSubmit(doLogin)}>
                    <UI.Form className="mb-3">
                        <UI.Form.Label>
                            <Trans>screens.auth.emailaddress</Trans>
                        </UI.Form.Label>
                        <UI.Form.Control
                            {...register("email", { required: true })}
                            type="email"
                            isInvalid={errors.email !== undefined}
                            placeholder={t("screens.auth.emailaddress_placeholder")}
                            minLength={1}
                        />
                    </UI.Form>
                    <UI.Form className="mb-3">
                        <UI.Form.Label>
                            <Trans>screens.auth.password</Trans>
                        </UI.Form.Label>
                        <UI.Form.Control
                            {...register("password", { required: true })}
                            type="password"
                            isInvalid={errors.password !== undefined}
                            placeholder={t("screens.auth.password_placeholder")}
                            minLength={1}
                        />
                    </UI.Form>
                    <UI.Button type="submit" label={t("screens.auth.login")} />
                    {error && (
                        <div className={`${styles.loginErrorContainer} mt-3`}>
                            <UI.Icon icon={UI.SVGIcon.CloseCircle} color={"#721c24"} />
                            {error.message}
                            {erpUserLoginError !== ERP_LOGIN_ERROR_TYPE.NONE ? (
                                <ErpInformationLink
                                    filepath={
                                        erpUserLoginError === ERP_LOGIN_ERROR_TYPE.IT2
                                            ? FILEPATH_ERP_HINT_IMMOTOP2
                                            : FILEPATH_ERP_HINT_RIMOR5
                                    }
                                />
                            ) : undefined}
                        </div>
                    )}
                </UI.Form>
            </UI.Container>
        );
    }
};

export const Login = Sentry.withProfiler(observer(LoginBase));
